import { Button, Group, Stack } from '@mantine/core'
import { closeAllModals, openModal } from '@mantine/modals'
import { useQueryClient } from '@tanstack/react-query'
import {
  changeAlertStatus,
  changeAlertStatusSchema,
} from 'api/alerts/changeAlertStatus'
import { T } from 'components/common/T'
import { alertStatusOptions } from 'data/alertStatus'
import { useFormMutation } from 'hooks/useFormMutation'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BiLoader } from 'react-icons/bi'
import { TbTimelineEventText } from 'react-icons/tb'
import { useParams } from 'react-router-dom'
import { capitalizeFirstLetter } from 'shared/utils'
import { HookFormError, InputSelect, InputSubmit, InputTextarea } from '..'

export const openAlertStatusModal = (alertId: string) => {
  openModal({
    title: <T>alert.changeStatus</T>,
    children: <ChangeAlertStatusModal alertId={alertId} />,
  })
}

export const ChangeAlertStatusButton = () => {
  const { t } = useTranslation()
  const { alertId } = useParams()

  return (
    <Button
      color="brand"
      leftSection={<TbTimelineEventText size="1.125rem" />}
      onClick={() => openAlertStatusModal(alertId!)}
      radius="xl"
      size="sm"
      variant="light"
    >
      {t('alert.changeStatus')}
    </Button>
  )
}

const ChangeAlertStatusModal = ({ alertId }: { alertId: string }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { methods, onSubmit } = useFormMutation({
    mutationFn: changeAlertStatus(alertId),
    schema: changeAlertStatusSchema,
    onSuccess: () => {
      queryClient.invalidateQueries(['alert', alertId])
      queryClient.invalidateQueries(['alerts'])
      closeAllModals()
    },
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <Stack>
          <InputSelect
            name="status"
            label={t('generic.status')}
            required
            icon={BiLoader}
            data={alertStatusOptions(t).slice(0, 2)}
            withinPortal
            zIndex={1004}
          />
          <InputTextarea
            name="comment"
            label={capitalizeFirstLetter(t('alert.comment'))}
          />
          <HookFormError />
          <Group justify="right">
            <Button
              variant={'outline'}
              type="button"
              onClick={() => closeAllModals()}
            >
              {t('generic.cancel')}
            </Button>
            <InputSubmit mt={0} value={t('generic.save')} />
          </Group>
        </Stack>
      </form>
    </FormProvider>
  )
}
